import React, { useState, useEffect, useRef } from 'react';
import sanitizeHtml from 'sanitize-html';
import { Backdrop, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/i18n';
import API_ROUTES from '../config/api';
import { apiFetch } from '../apiFetch';
import SessionFinderWizard, { WizardValues } from './SessionFinderWizard';

const direction = i18n.language === 'he' ? 'rtl' : 'ltr';

interface SessionLevel {
  LEVEL_NAME: string;
}

const FCM_TOKEN_HASH_KEY = 'srf_fcm_token_hash';
const VERIFICATION_CODE_KEY = 'srf_verification_code';

async function hashString(str: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

async function fetchWithRetry(url: string, options: RequestInit = {}, maxRetries = 3): Promise<Response> {
  let lastError: Error = new Error('Unknown error');
  for (let i = 0; i <= maxRetries; i++) {
    try {
      const response = await apiFetch(url, options);
      if (response.ok || i === maxRetries) return response;
    } catch (e) {
      lastError = e as Error;
    }
    await new Promise(r => setTimeout(r, 1000 * (i + 1)));
  }
  throw lastError;
}

const DEFAULT_VALUES: WizardValues = {
  selectedLevels: [],
  sidePreference: 'any',
  preferredDayOfWeek: [0],
  preferredTimeOfDay: [],
  minSpots: 0,
  startTime: 12,
  email: '',
  verificationCode: localStorage.getItem(VERIFICATION_CODE_KEY) || '',
  wantsPush: false,
  pushToken: '',
};

const SessionFinderWizardPage: React.FC = () => {
  const { t } = useTranslation();
  const [sessionLevels, setSessionLevels] = useState<SessionLevel[]>([]);
  const [initialValues, setInitialValues] = useState<WizardValues>(DEFAULT_VALUES);
  const [preferenceGUID, setPreferenceGUID] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const watcherRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    loadSessionLevels();
    const params = new URLSearchParams(window.location.search);
    const pid    = params.get('pid');
    const action = params.get('action');
    const code   = params.get('code');

    if (code) localStorage.setItem(VERIFICATION_CODE_KEY, code);
    if (pid && action === 'activate' && code) {
      activateFromUrl(pid, code);   // loads preference + activates sequentially
    } else if (pid && action === 'stop') {
      stopFromUrl(pid);
    } else if (pid) {
      loadPreference(pid);
    }

    const onAppAlert = (e: Event) => {
      const { type, text } = (e as CustomEvent).detail;
      setMessage({ type, text });
      setShowAlert(true);
    };
    window.addEventListener('app:alert', onAppAlert);
    return () => {
      window.removeEventListener('app:alert', onAppAlert);
      watcherRef.current?.();
    };
  }, []);

  const loadSessionLevels = async () => {
    try {
      const res = await fetchWithRetry(API_ROUTES.sessionLevels, {}, 3);
      if (res.ok) setSessionLevels(await res.json());
    } catch {
      // non-fatal — wizard works without levels
    }
  };

  const loadPreference = async (guid: string) => {
    try {
      const res = await fetchWithRetry(`${API_ROUTES.preferences}/get/${guid}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }, 2);
      if (!res.ok) return;
      const pref = await res.json();

      let token = '';
      if (pref.FCM_TOKEN) {
        const localHash = localStorage.getItem(FCM_TOKEN_HASH_KEY);
        const prefHash = await hashString(pref.FCM_TOKEN);
        if (localHash && prefHash === localHash) {
          token = pref.FCM_TOKEN;
        } else {
          localStorage.removeItem(FCM_TOKEN_HASH_KEY);
        }
      }

      setPreferenceGUID(pref.GUID || guid);
      setInitialValues({
        selectedLevels: pref.FOR_LEVELS || [],
        sidePreference: pref.SIDE_PREFERENCE?.[0] || 'any',
        preferredDayOfWeek: pref.PREFERRED_DAY_OF_WEEK || [0],
        preferredTimeOfDay: pref.PREFERRED_TIME_OF_DAY || [],
        minSpots: pref.AVAILABLE_MIN || 0,
        startTime: pref.START_TIME || 12,
        email: pref.CHANNEL_USER_IDENTITY || '',
        verificationCode: localStorage.getItem(VERIFICATION_CODE_KEY) || '',
        wantsPush: !!token,
        pushToken: token,
      });
    } catch {
      // non-fatal
    }
  };

  const handleSubmit = async (values: WizardValues) => {
    setLoading(true);
    const fcmToken = values.pushToken;

    if (fcmToken) {
      const hash = await hashString(fcmToken);
      localStorage.setItem(FCM_TOKEN_HASH_KEY, hash);
    }

    try {
      const body = {
        CHANNEL: 'EMAIL',
        CHANNEL_USER_IDENTITY: values.email,
        VERIFICATION_CODE: localStorage.getItem(VERIFICATION_CODE_KEY) || values.verificationCode,
        DAYS_AHEAD: -1,
        FOR_LEVELS: values.selectedLevels,
        SIDE_PREFERENCE: values.sidePreference === 'any' ? [] : [values.sidePreference],
        PREFERRED_DAY_OF_WEEK: values.preferredDayOfWeek,
        AVAILABLE_MIN: values.minSpots,
        EXPIRY_PERIOD: 4,
        START_TIME: values.startTime,
        PREFERRED_TIME_OF_DAY: values.preferredTimeOfDay,
        GUID: preferenceGUID || undefined,
        FCM_TOKEN: fcmToken || undefined,
      };

      const res = await apiFetch(`${API_ROUTES.preferences}/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const result = await res.json();

      if (res.ok) {
        const pid = result.pid;
        setPreferenceGUID(pid);
        const url = new URL(window.location.href);
        url.searchParams.set('pid', pid);
        window.history.replaceState({}, '', url.toString());
        setMessage({ type: 'success', text: t('dialogs.submit.success') });
        setShowAlert(true);
      } else {
        const detail = result.detail;
        const errorKey = typeof detail === 'object' ? detail?.error_key : null;
        const errorMsg = typeof detail === 'object' ? detail?.message : detail;
        if (errorKey === 'verification.link_sent') {
          setMessage({ type: 'success', text: errorMsg });
          setShowAlert(true);
          startWatchingForActivation(localStorage.getItem(VERIFICATION_CODE_KEY));
          return;
        }
        if (errorKey === 'verification.rate_limited') {
          setMessage({ type: 'error', text: errorMsg });
          setShowAlert(true);
          return;
        }
        setMessage({ type: 'error', text: errorMsg || t('dialogs.submit.error') });
        setShowAlert(true);
      }
    } catch {
      setMessage({ type: 'error', text: t('form.serverError') });
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const activateFromUrl = async (pid: string, code: string) => {
    // loadPreference with retries acts as cold-start probe — backend must respond before activate fires.
    await loadPreference(pid);
    try {
      const res = await apiFetch(
        `${API_ROUTES.preferenceAction}?pid=${pid}&action=activate&code=${encodeURIComponent(code)}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      const result = await res.json();
      setMessage({
        type: res.ok ? 'success' : 'error',
        text: res.ok
          ? result.message
          : (typeof result.detail === 'object' ? result.detail?.message : result.detail) || t('dialogs.action.error'),
      });
      setShowAlert(true);
    } catch {
      setMessage({ type: 'error', text: t('dialogs.action.serverError') });
      setShowAlert(true);
    }
  };

  const stopFromUrl = async (pid: string) => {
    await loadPreference(pid);
    try {
      const res = await apiFetch(
        `${API_ROUTES.preferenceAction}?pid=${pid}&action=stop_now`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      const result = await res.json();
      setMessage({
        type: res.ok ? 'success' : 'error',
        text: res.ok
          ? result.message
          : (typeof result.detail === 'object' ? result.detail?.message : result.detail) || t('dialogs.action.error'),
      });
      setShowAlert(true);
    } catch {
      setMessage({ type: 'error', text: t('dialogs.action.serverError') });
      setShowAlert(true);
    }
  };

  const startWatchingForActivation = (codeSnapshot: string | null) => {
    // Stop any existing watcher before starting a new one (e.g. user re-submits while waiting)
    watcherRef.current?.();
    watcherRef.current = null;

    const check = () => {
      if (localStorage.getItem(VERIFICATION_CODE_KEY) !== codeSnapshot) {
        stopWatching();
        setShowAlert(false);
        setMessage({ type: 'success', text: t('wizard.alertsActivated') });
        setShowAlert(true);
      }
    };

    const poll = setInterval(check, 1000);
    const timeout = setTimeout(stopWatching, 10 * 60 * 1000);

    const onStorage = (e: StorageEvent) => { if (e.key === VERIFICATION_CODE_KEY) check(); };
    const onFocus = () => check();

    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);

    function stopWatching() {
      clearInterval(poll);
      clearTimeout(timeout);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
      watcherRef.current = null;
    }

    watcherRef.current = stopWatching;
  };

  return (
    <>
      <SessionFinderWizard
        sessionLevels={sessionLevels}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        isNew={!preferenceGUID}
      />
      <Dialog
        open={showAlert}
        onClose={() => setShowAlert(false)}
        maxWidth="sm"
        fullWidth
        dir={direction}
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          {message?.type === 'success' ? t('dialogs.success.title') : t('dialogs.error.title')}
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body1"
            sx={{ textAlign: 'center', py: 2 }}
            component="div"
            dangerouslySetInnerHTML={
              message?.text
                ? { __html: sanitizeHtml(message.text, { allowedTags: ['b', 'br'], allowedAttributes: { b: [] } }) }
                : undefined
            }
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button
            onClick={() => setShowAlert(false)}
            variant="contained"
            color={message?.type === 'success' ? 'success' : 'error'}
          >
            {t('dialogs.close')}
          </Button>
        </DialogActions>
      </Dialog>
      <Backdrop open={loading} sx={{ zIndex: (theme) => theme.zIndex.modal + 1, color: '#fff' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
        </Box>
      </Backdrop>
    </>
  );
};

export default SessionFinderWizardPage;
